import type { AccountTagEnum, RequestTypeEnum } from "@nlc/shared-types";

import { Avatar } from "@/components/avatar";
import { Badge } from "@/components/badge";
import { Link } from "@/components/link";
import {
  NonFousableTooltipTarget,
  Tooltip,
  TooltipTrigger,
} from "@/components/tooltip";
import { COMPLEXITY_OPTIONS } from "@/features/shared/constants/complexityConstants";
import { getDueDateDisplay } from "@/utils/dueDateDisplay";
import { renderRequestDesignType } from "@/utils/requestFormatters";
import { convertUTCtoEastern } from "@/utils/timezoneConverter";

import { RequestStatusBadge } from "../../components/RequestStatusBadge";

// Common cell renderers used across multiple table configurations

export const nameCell = (value: unknown, row: { id: number }) => {
  const name = (typeof value === "string" ? value : "") || "Untitled";

  return (
    <div className="max-w-full overflow-hidden">
      <Link
        className="font-medium text-text-primary hover:no-underline cursor-pointer line-clamp-3 break-words"
        href={`/requests/${row.id}/edit`}
      >
        {name}
      </Link>
    </div>
  );
};

export const queuedNameCell = (
  value: unknown,
  row: { id: number; queuePosition?: number },
) => {
  const name = (typeof value === "string" ? value : "") || "Untitled";

  return (
    <div className="flex flex-col gap-2 max-w-full overflow-hidden">
      <Link
        className="font-medium text-text-primary hover:no-underline cursor-pointer line-clamp-3 break-words"
        href={`/requests/${row.id}/edit`}
      >
        {name}
      </Link>
    </div>
  );
};

export const statusCell = (value: unknown) => {
  if (
    !value ||
    typeof value !== "object" ||
    !("id" in value) ||
    !("name" in value)
  ) {
    return <span className="text-text-tertiary">--</span>;
  }

  return (
    <div className="line-clamp-3 break-words">
      <RequestStatusBadge status={value as { id: number; name: string }} />
    </div>
  );
};

export const brandCell = (value: unknown) => {
  if (!value || typeof value !== "object" || !("name" in value)) {
    return <span className="text-text-tertiary">--</span>;
  }

  const brandValue = value as {
    id: number;
    name: string;
    profilePicture?: string;
  };

  const displayName = brandValue.name || "Untitled";

  return (
    <div className="flex items-center py-3 pr-3 min-w-0">
      <Avatar
        alt={displayName}
        className="size-6 rounded-full border border-border-secondary object-cover shrink-0"
        src={brandValue.profilePicture}
      />
      <p className="ml-3 text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-3 break-words">
        {displayName}
      </p>
    </div>
  );
};

export const createdByCell = (value: unknown) => {
  if (!value || typeof value !== "object" || !("name" in value)) {
    return <span className="text-text-tertiary">--</span>;
  }
  const userValue = value as { id: number; name: string; role?: number };

  return (
    <div className="line-clamp-3 break-words">
      <span className="text-sm font-medium text-text-primary line-clamp-3 break-words block">
        {userValue.name}
      </span>
    </div>
  );
};

export const assigneeCell = (value: unknown) => {
  if (!value || typeof value !== "object" || !("name" in value)) {
    return <span className="text-text-tertiary">--</span>;
  }
  const userValue = value as { designation?: string; id: number; name: string };

  return (
    <div className="line-clamp-3 break-words">
      <span className="text-sm font-medium text-text-primary">
        {userValue.name}
      </span>
    </div>
  );
};

export const complexityCell = (value: unknown) => {
  if (!value || typeof value !== "string") {
    return <span className="text-text-tertiary">--</span>;
  }

  // Use COMPLEXITY_OPTIONS with find() for consistent lookup (same as InlineComplexityCell)
  const currentOption = COMPLEXITY_OPTIONS.find(
    (opt) => opt.stringValue === value,
  );

  return currentOption ? (
    <div className="inline-flex">
      <Badge isRounded size="small" variant={currentOption.variant}>
        {currentOption.name}
      </Badge>
    </div>
  ) : (
    <span className="text-text-tertiary">--</span>
  );
};

export const dueDateCellColored = (value: unknown) => {
  if (!value || typeof value !== "string") {
    return <span className="text-text-tertiary">--</span>;
  }
  const { badgeVariant, colorClass, isPill, label } = getDueDateDisplay(value);

  return (
    <div className="line-clamp-3 break-words">
      {isPill ? (
        <Badge isRounded size="small" variant={badgeVariant}>
          {label}
        </Badge>
      ) : (
        <span className={`text-sm font-medium ${colorClass}`}>{label}</span>
      )}
    </div>
  );
};

export const dateCell = (value: unknown) => {
  if (!value || typeof value !== "string") {
    return <span className="text-text-tertiary">--</span>;
  }

  return (
    <div className="line-clamp-3 break-words">
      <span className="text-sm">{convertUTCtoEastern(value)}</span>
    </div>
  );
};

export const requestDesignTypeCell = (
  _value: unknown,
  row: { designType?: string; requestType: string },
) => {
  // Ensure we have a valid requestType - fallback to value if row.requestType is missing
  const requestType = row.requestType || (_value as string);
  const designType = row.designType;

  return renderRequestDesignType(requestType as RequestTypeEnum, designType);
};

export const designTypeCell = (
  _value: unknown,
  row: { designType?: string },
) => {
  const designType = row.designType?.trim();

  if (!designType) {
    return <span className="text-text-tertiary">--</span>;
  }

  return (
    <div className="py-3 pr-3 min-w-0 w-full line-clamp-3 break-words">
      <span className="text-text-primary text-sm font-medium">
        {designType}
      </span>
    </div>
  );
};

export const accountCell = (value: unknown) => {
  if (!value || typeof value !== "object" || !("name" in value)) {
    return <span className="text-text-tertiary">--</span>;
  }

  const accountValue = value as { id: number; name: string };

  return (
    <Link
      className="text-sm font-medium text-text-primary hover:no-underline cursor-pointer no-underline line-clamp-3 break-words block max-w-full"
      href={`/projects/${accountValue.id}`}
    >
      {accountValue.name}
    </Link>
  );
};

interface ActiveSlots {
  graphic: number;
  maxTotal: number;
  total: number;
  video: number;
  website: number;
}

/**
 * Account cell with active slots tooltip (Team Lead only)
 * Shows breakdown of active slots when hovering over the account name
 */
export const accountCellWithSlotTooltip = (value: unknown) => {
  if (!value || typeof value !== "object" || !("name" in value)) {
    return <span className="text-text-tertiary">--</span>;
  }

  const accountValue = value as {
    activeSlots?: ActiveSlots;
    id: number;
    name: string;
    tags?: AccountTagEnum[];
  };

  const activeSlots = accountValue.activeSlots;

  // Build tooltip content showing slot breakdown
  const buildTooltipContent = () => {
    if (!activeSlots) {
      return "Design slots data unavailable";
    }

    const parts = [];

    if (activeSlots.graphic > 0) {
      parts.push(`${activeSlots.graphic} graphic`);
    }

    if (activeSlots.video > 0) {
      parts.push(`${activeSlots.video} video`);
    }

    if (activeSlots.website > 0) {
      parts.push(`${activeSlots.website} website`);
    }

    if (parts.length === 0) {
      return `0/${activeSlots.maxTotal} design slots`;
    }

    return `${activeSlots.total}/${activeSlots.maxTotal} slots: ${parts.join(", ")}`;
  };

  return (
    <TooltipTrigger delay={250}>
      <NonFousableTooltipTarget>
        <Link
          className="text-sm font-medium text-text-primary hover:no-underline cursor-pointer no-underline line-clamp-3 break-words block max-w-full"
          href={`/projects/${accountValue.id}`}
        >
          {accountValue.name}
        </Link>
      </NonFousableTooltipTarget>
      <Tooltip>{buildTooltipContent()}</Tooltip>
    </TooltipTrigger>
  );
};

// SuperAdmin-specific cell renderers with inline editing

export const dueDateCellWithInternal = (
  value: unknown,
  row: {
    customerDueDate?: null | string;
    dueDate?: null | string;
    internalDueDate?: null | string;
  },
) => {
  // Priority: internalDueDate ?? customerDueDate ?? dueDate
  const displayDate =
    row.internalDueDate ?? row.customerDueDate ?? row.dueDate ?? value;

  if (!displayDate || typeof displayDate !== "string") {
    return <span className="text-text-tertiary">--</span>;
  }

  const { badgeVariant, colorClass, isPill, label } =
    getDueDateDisplay(displayDate);

  return (
    <div className="line-clamp-3 break-words">
      {isPill ? (
        <Badge isRounded size="small" variant={badgeVariant}>
          {label}
        </Badge>
      ) : (
        <span className={`text-sm font-medium ${colorClass}`}>{label}</span>
      )}
    </div>
  );
};
